import type {
    ChatAgUiStateChange,
    ChatSnapshot,
    ChatStatusStateChange,
} from './chat-state';
import {ChatScrollController} from './chat-ui/scroll-controller';
import {ChatStatusArea} from './chat-ui/status-area';
import {ChatTranscriptRenderer} from './chat-ui/transcript-renderer';

declare const howspace: any;

type Labels = {
    placeholder: string;
    send: string;
    emptyState: string;
    thinking: string;
    thoughtFor: string;
    fewSeconds: string;
    seconds: string;
    copy: string;
    close: string;
    expandStatus: string;
    collapseStatus: string;
    toolCalls: string;
    toolStatusRunning: string;
    toolStatusDone: string;
    toolStatusError: string;
    toolArguments: string;
    toolResult: string;
    modelChangedWarning: string;
    threadStaleWarning: string;
    runTimeoutWarning: string;
    inputContextTooLargeWarning: string;
    queueDelayNotice: string;
    error: string;
    addContextFile: string;
    contextFileQueued: string;
    contextFileProcessing: string;
    contextFileReady: string;
    contextFileFailed: string;
    contextFileGeneratePageAction: string;
    contextFileCreateWidgetAction: string;
    contextFileMaxSourceFileSize: string;
    contextFileMaxCreatedAssetCount: string;
    contextFileMaxCreatedAssetFileSize: string;
};

export type ChatUiOptions = {
    labels: Labels;
    showReasoning: boolean;
    showToolCalls: boolean;
    showFriendlyToolStatus: boolean;
    toolLabels: Record<string, string>;
    modelMismatch: boolean;
    workspaceContextUpload: boolean;
    openingQuestion?: string;
    onSubmit: (message: string) => void;
    onAddContextFile?: (file?: File) => void;
};

export class ChatUi {
    private readonly root: HTMLElement;
    private readonly messages: HTMLElement;
    private readonly form: HTMLFormElement;
    private readonly textarea: HTMLTextAreaElement;
    private readonly submitButton: HTMLButtonElement;
    private readonly addContextButton: HTMLButtonElement | null = null;
    private readonly statusArea: ChatStatusArea;
    private readonly scrollController: ChatScrollController;
    private readonly transcriptRenderer: ChatTranscriptRenderer;
    private running = false;
    private contextUploadBusy = false;

    constructor(container: HTMLElement, private readonly options: ChatUiOptions) {
        this.root = document.createElement('div');
        this.root.className = 'howspace-ai-chat';

        if (options.modelMismatch) {
            const warning = document.createElement('div');
            warning.className = 'howspace-ai-chat-warning';

            const warningText = document.createElement('span');
            warningText.className = 'howspace-ai-chat-warning-text';
            warningText.textContent = options.labels.modelChangedWarning;

            const closeLabel = options.labels.close || 'Close';
            const closeButton = document.createElement('button');
            closeButton.type = 'button';
            closeButton.className = 'howspace-ai-chat-warning-close';
            closeButton.setAttribute('aria-label', closeLabel);
            closeButton.title = closeLabel;
            closeButton.innerHTML = '<i class="fa-light fa-xmark" aria-hidden="true"></i>';
            closeButton.addEventListener('click', () => {
                warning.remove();
            });

            warning.append(warningText, closeButton);
            this.root.append(warning);
        }

        this.messages = document.createElement('div');
        this.messages.className = 'howspace-ai-chat-messages';
        this.messages.setAttribute('aria-live', 'polite');
        this.root.append(this.messages);
        this.scrollController = new ChatScrollController(this.messages);

        const status = document.createElement('div');
        status.className = 'howspace-ai-chat-status';
        status.setAttribute('aria-live', 'polite');
        this.root.append(status);
        this.statusArea = new ChatStatusArea(status, {
            labels: this.options.labels,
            isRunning: () => this.running,
            onSubmit: message => this.options.onSubmit(message),
            focusComposer: () => this.focusComposer(),
            setContextUploadBusy: busy => this.setContextUploadBusy(busy),
        });
        this.transcriptRenderer = new ChatTranscriptRenderer(this.messages, this.scrollController, {
            labels: this.options.labels,
            showReasoning: this.options.showReasoning,
            showToolCalls: this.options.showToolCalls,
            showFriendlyToolStatus: this.options.showFriendlyToolStatus,
            toolLabels: this.options.toolLabels,
            openingQuestion: this.options.openingQuestion,
            isRunning: () => this.running,
        });

        this.form = document.createElement('form');
        this.form.className = 'howspace-ai-chat-composer';

        const inputShell = document.createElement('div');
        inputShell.className = 'howspace-ai-chat-composer-input';
        if (options.workspaceContextUpload) {
            inputShell.classList.add('has-context-upload');
            this.addContextButton = this.renderAddContextButton();
        }

        this.textarea = document.createElement('textarea');
        this.textarea.className = 'howspace-ai-chat-input';
        this.textarea.rows = 1;
        this.textarea.placeholder = options.labels.placeholder;
        this.textarea.setAttribute('aria-label', options.labels.placeholder);

        this.submitButton = document.createElement('button');
        this.submitButton.type = 'submit';
        this.submitButton.className = 'howspace-ai-chat-send';
        this.submitButton.setAttribute('aria-label', options.labels.send);
        this.submitButton.title = options.labels.send;
        this.submitButton.innerHTML =
            '<i class="fa-light fa-arrow-down howspace-ai-chat-send-icon" aria-hidden="true"></i>';

        if (this.addContextButton !== null) {
            inputShell.append(this.addContextButton);
        }
        inputShell.append(this.textarea, this.submitButton);
        this.form.append(inputShell);
        this.root.append(this.form);
        container.prepend(this.root);

        this.form.addEventListener('submit', event => {
            event.preventDefault();
            const message = this.textarea.value.trim();
            if (!message || this.running || this.submitButton.disabled) {
                return;
            }
            this.options.onSubmit(message);
            this.textarea.value = '';
            this.resizeTextarea();
            this.updateSubmitState();
        });

        this.textarea.addEventListener('input', () => {
            this.resizeTextarea();
            this.updateSubmitState();
        });

        this.textarea.addEventListener('keydown', event => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this.form.requestSubmit();
            }
        });

        if (options.workspaceContextUpload) {
            this.root.addEventListener('dragover', this.handleFileDragOver);
            this.root.addEventListener('dragenter', this.handleFileDragOver);
            this.root.addEventListener('dragleave', this.handleFileDragLeave);
            this.root.addEventListener('drop', this.handleFileDrop);
        }

        this.resizeTextarea();
        this.updateSubmitState();
        this.focusComposer();
    }

    private renderAddContextButton(): HTMLButtonElement {
        const label = this.options.labels.addContextFile || 'Add context for the AI';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-chat-add-context';
        button.setAttribute('aria-label', label);

        const icon = document.createElement('span');
        icon.className = 'howspace-ai-chat-add-context-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '+';

        const tooltip = document.createElement('span');
        tooltip.className = 'howspace-ai-chat-add-context-tooltip';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.textContent = label;

        button.append(icon, tooltip);
        button.addEventListener('click', () => {
            if (button.disabled || this.contextUploadBusy || this.running) {
                return;
            }
            this.options.onAddContextFile?.();
        });
        return button;
    }

    private readonly handleFileDragOver = (event: DragEvent): void => {
        if (!this.isFileDrag(event)) {
            return;
        }

        event.preventDefault();
        if (this.canAcceptFileDrop(event)) {
            this.root.classList.add('is-file-drag-over');
        } else {
            this.root.classList.remove('is-file-drag-over');
        }
    };

    private readonly handleFileDragLeave = (event: DragEvent): void => {
        if (event.relatedTarget instanceof Node && this.root.contains(event.relatedTarget)) {
            return;
        }

        this.root.classList.remove('is-file-drag-over');
    };

    private readonly handleFileDrop = (event: DragEvent): void => {
        if (!this.isFileDrag(event)) {
            return;
        }

        event.preventDefault();
        this.root.classList.remove('is-file-drag-over');
        if (!this.canAcceptFileDrop(event)) {
            return;
        }

        const file = event.dataTransfer?.files?.[0] || null;
        if (file instanceof File) {
            this.options.onAddContextFile?.(file);
        }
    };

    private canAcceptFileDrop(event: DragEvent): boolean {
        return this.options.workspaceContextUpload
            && !this.contextUploadBusy
            && !this.running
            && event.dataTransfer !== null
            && (event.dataTransfer.files.length === 0 || event.dataTransfer.files.length === 1);
    }

    private isFileDrag(event: DragEvent): boolean {
        return event.dataTransfer !== null
            && Array.from(event.dataTransfer.types).includes('Files');
    }

    // Bootstrap/replay can build the whole transcript. Streaming updates must
    // go through applyChange so completed history stays inert.
    renderInitial(snapshot: ChatSnapshot): void {
        this.setRunning(snapshot.running);
        this.statusArea.render(snapshot);
        this.updateSubmitState();

        if (this.transcriptRenderer.renderInitial(snapshot)) {
            this.scrollToBottom();
        }
    }

    applyAgUiChange(change: ChatAgUiStateChange | null): void {
        if (!change) {
            return;
        }

        const shouldFollowBottom = this.scrollController.shouldFollowBottom();
        this.setRunning(change.running);
        this.statusArea.render(change);
        const changed = this.transcriptRenderer.applyChange(change);

        this.updateSubmitState();
        if (change.event.type === 'RUN_FINISHED' || change.event.type === 'RUN_ERROR') {
            this.focusComposer();
        }
        if (changed && shouldFollowBottom) {
            this.scrollToBottom();
        }
    }

    applyStatusChange(change: ChatStatusStateChange | null): void {
        if (!change) {
            return;
        }

        this.setRunning(change.running);
        this.statusArea.render(change);
        this.updateSubmitState();
    }

    destroy(): void {
        this.scrollController.destroy();
        this.transcriptRenderer.destroy();
        this.root.remove();
    }

    setDraft(message: string): void {
        this.textarea.value = message;
        this.resizeTextarea();
        this.updateSubmitState();
    }

    startQueuedRunNoticeTimer(): void {
        this.transcriptRenderer.startQueuedRunNoticeTimer();
    }

    clearQueuedRunNotice(): void {
        this.transcriptRenderer.clearQueuedRunNotice();
    }

    private setRunning(running: boolean): void {
        this.running = running;
        if (!running) {
            this.transcriptRenderer.clearQueuedRunNotice();
        }
        const value = String(running);
        if (this.root.dataset.running !== value) {
            this.root.dataset.running = value;
        }
    }

    focusComposer(): void {
        window.requestAnimationFrame(() => {
            if (this.root.isConnected) {
                this.textarea.focus({preventScroll: true});
            }
        });
    }

    private setContextUploadBusy(busy: boolean): void {
        if (this.contextUploadBusy === busy) {
            return;
        }

        this.contextUploadBusy = busy;
        this.root.dataset.contextUploadBusy = String(busy);
        this.updateSubmitState();
    }

    scrollToBottom(): void {
        this.scrollController.scrollToBottom();
    }

    private resizeTextarea(): void {
        this.textarea.style.height = 'auto';
        this.textarea.style.height = `${this.textarea.scrollHeight}px`;
    }

    private updateSubmitState(): void {
        const unavailable = isActivityIdle();
        if (this.textarea.disabled !== unavailable) {
            this.textarea.disabled = unavailable;
        }

        const disabled = unavailable || this.running || this.textarea.value.trim() === '';
        if (this.submitButton.disabled !== disabled) {
            this.submitButton.disabled = disabled;
        }

        if (this.addContextButton !== null) {
            const addDisabled = unavailable || this.running || this.contextUploadBusy;
            if (this.addContextButton.disabled !== addDisabled) {
                this.addContextButton.disabled = addDisabled;
            }
        }
    }
}

const isActivityIdle = (): boolean => (
    typeof howspace !== 'undefined'
        && typeof howspace.activityTracker?.isIdle === 'function'
        && howspace.activityTracker.isIdle()
);
